import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PHASE_OF_PLAY_META,
  GRASSROOTS_PHASE_META,
  projectToGrassrootsPhase,
  tierForCoachLevel,
  type PhaseOfPlay,
  type GrassrootsPhase,
} from '@4cm/core/phaseOfPlay';
import { BrainOrb, Card, EmptyState, GlassPill, Skeleton } from '../components/primitives';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useTeamSessionCoverage } from '../lib/queries/useTeamSessionCoverage';
import { useCalibration } from '../contexts/CalibrationContext';
import { bucketByWeek, detectDeficit } from '../lib/curriculumCoverage';
import './page-shell.css';
import './Curriculum.css';

const PHASES: PhaseOfPlay[] = ['attacking', 'defending', 'transitions', 'individual'];

// Map an academy phase to the grassroots phase it folds into. Used when
// the active tier is grassroots to merge attacking + individual minutes
// under the in_possession column.
const ACADEMY_TO_GRASSROOTS_DISPLAY: Record<PhaseOfPlay, GrassrootsPhase> = {
  attacking: 'in_possession',
  defending: 'out_of_possession',
  transitions: 'transition',
  individual: 'in_possession',
};

function formatWeekRange(weekStart: Date): string {
  const end = new Date(weekStart);
  end.setUTCDate(end.getUTCDate() + 6);
  const startStr = weekStart.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  const endStr = end.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return `${startStr} – ${endStr}`;
}

export function Curriculum() {
  const navigate = useNavigate();
  const { team, loading: teamLoading } = useActiveTeam();
  const { coachLevel } = useCalibration();
  const tier = tierForCoachLevel(coachLevel);
  const { rows, state, errorMessage } = useTeamSessionCoverage(team?.id, teamLoading);

  const weeks = useMemo(() => bucketByWeek(rows), [rows]);
  const deficit = useMemo(() => detectDeficit(rows), [rows]);

  // Tier-aware column list — 4 academy phases or 3 grassroots phases.
  const columns: { id: string; label: string; glyph: string }[] = useMemo(() => {
    if (tier === 'grassroots') {
      return ['in_possession', 'out_of_possession', 'transition'].map((id) => {
        const meta = GRASSROOTS_PHASE_META[id as GrassrootsPhase];
        return { id, label: meta.label, glyph: meta.glyph };
      });
    }
    return PHASES.map((p) => ({ id: p, label: PHASE_OF_PLAY_META[p].label, glyph: PHASE_OF_PLAY_META[p].glyph }));
  }, [tier]);

  // For each week, compute pct per column in the active tier vocabulary.
  const weekPercents = useMemo(() => {
    return weeks.map((w) => {
      const pcts: Record<string, number> = {};
      for (const col of columns) pcts[col.id] = 0;
      if (w.totalMinutes <= 0) return { week: w, pcts };
      for (const phase of PHASES) {
        const colId = tier === 'grassroots' ? ACADEMY_TO_GRASSROOTS_DISPLAY[phase] : phase;
        pcts[colId] += (w.minutesByPhase[phase] / w.totalMinutes) * 100;
      }
      return { week: w, pcts };
    });
  }, [weeks, columns, tier]);

  const showDeficit = state === 'ok' && deficit.belowThreshold.length > 0;
  // Project the deficit phases to the active tier vocabulary for display.
  const deficitDisplayPhases = useMemo(() => {
    if (!showDeficit) return [];
    if (tier === 'grassroots') {
      // De-duplicate after projection (attacking + individual both fold to in_possession).
      const set = new Set(deficit.belowThreshold.map((p) => projectToGrassrootsPhase(p)));
      return Array.from(set).map((g) => GRASSROOTS_PHASE_META[g].label);
    }
    return deficit.belowThreshold.map((p) => PHASE_OF_PLAY_META[p].label);
  }, [deficit, showDeficit, tier]);

  return (
    <main className="main page curriculum-page">
      <header className="page-head">
        <BrainOrb size="lg" ariaLabel="Brain" />
        <div>
          <span className="page-eyebrow">Layer 1 · season planning</span>
          <h1 className="page-title">Curriculum</h1>
          <p className="page-sub">
            Week-by-week phase coverage. Spot deficits before they accumulate.
          </p>
        </div>
      </header>

      {state === 'loading' && (
        <Card variant="opaque">
          <Skeleton variant="text" width="40%" height={18} />
          <div style={{ height: 12 }} />
          <Skeleton variant="card" />
          <div style={{ height: 12 }} />
          <Skeleton variant="card" />
        </Card>
      )}
      {state === 'error' && (
        <Card variant="opaque">
          <p className="page-empty">
            Couldn&apos;t load coverage.{errorMessage ? ` (${errorMessage})` : null}
          </p>
        </Card>
      )}
      {state === 'empty' && (
        <EmptyState
          glyph="📅"
          title="No sessions yet"
          body="Plan one session and this heat-map starts to fill in. Brain can build it from a one-line description."
          cta={{ label: 'Plan a session', onClick: () => navigate('/sessions/new') }}
          hint={<>or press <kbd>⌘K</kbd> and ask Brain</>}
        />
      )}

      {showDeficit && (
        <Card variant="opaque" className="curriculum-deficit">
          <span className="page-eyebrow">⚠️ Coverage deficit (last 28 days)</span>
          <p className="curriculum-deficit__text">
            {deficitDisplayPhases.join(' and ')} is below 10% of session time over the last
            {' '}{deficit.sessionCount} session{deficit.sessionCount === 1 ? '' : 's'}.
            {' '}Worth pulling next session toward {deficitDisplayPhases.length === 1 ? 'it' : 'one of those'}.
          </p>
        </Card>
      )}

      {state === 'ok' && weekPercents.length > 0 && (
        <Card variant="opaque" className="curriculum-grid-card">
          <table className="curriculum-grid" aria-label="Phase coverage by week">
            <thead>
              <tr>
                <th scope="col" className="curriculum-grid__col-week">Week</th>
                <th scope="col" className="curriculum-grid__col-count">Sessions</th>
                {columns.map((col) => (
                  <th key={col.id} scope="col" className="curriculum-grid__col-phase">
                    <span aria-hidden="true">{col.glyph}</span> {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weekPercents.map(({ week, pcts }) => (
                <tr key={week.weekKey}>
                  <th scope="row" className="curriculum-grid__row-week">
                    <span className="curriculum-grid__week-key">{week.weekKey}</span>
                    <span className="curriculum-grid__week-range">{formatWeekRange(week.weekStart)}</span>
                  </th>
                  <td className="curriculum-grid__count">
                    <GlassPill mono>{week.sessionCount}</GlassPill>
                  </td>
                  {columns.map((col) => {
                    const pct = Math.round(pcts[col.id] ?? 0);
                    return (
                      <td
                        key={col.id}
                        className="curriculum-grid__cell"
                        data-empty={pct === 0 || undefined}
                        title={`${col.label}: ${pct}%`}
                      >
                        <div
                          className="curriculum-grid__heat"
                          style={{
                            // 0% → transparent; 100% → strong green. Sub-linear
                            // so 30% reads "decent" and 60%+ reads "dominant".
                            background: pct > 0
                              ? `rgba(46, 204, 113, ${Math.min(0.85, 0.15 + pct / 120)})`
                              : 'transparent',
                          }}
                        />
                        <span className="curriculum-grid__pct">{pct}%</span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <p className="curriculum-footnote">
        <Link to="/sessions/new">Plan a session</Link> to fill the next week, or{' '}
        <Link to="/sessions">browse the full session log</Link>.
      </p>
    </main>
  );
}
